import { memo } from 'react';
import type { TFunction } from 'i18next';
import { formatLedgerAction, formatLedgerDelta, formatLedgerNote, resolveLedgerRowTitle } from '../../utils/ledger-display';
import { tokenDisplayLabel } from '../../utils/orb-display';

export type LedgerHistoryRowData = {
  id: string;
  asset: string;
  action: string;
  delta: string;
  balance_after: string;
  note: string | null;
  create_time: string;
};

type Theme = {
  text: string;
  muted: string;
  softBorder: string;
  chipBg: string;
  gold: string;
  positive: string;
  negative: string;
};

function formatRowTime(iso: string): string {
  return new Date(iso).toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export const LedgerHistoryRow = memo(function LedgerHistoryRow({
  row,
  showBorder,
  theme,
  balanceAfterLabel,
  t,
}: {
  row: LedgerHistoryRowData;
  showBorder: boolean;
  theme: Theme;
  balanceAfterLabel: string;
  t: TFunction;
}) {
  const delta = formatLedgerDelta(row.delta);

  return (
    <div
      className="flex items-start gap-3 border-b px-4 py-3 last:border-b-0"
      style={{ borderColor: showBorder ? theme.softBorder : 'transparent' }}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ color: theme.text }}>
            {resolveLedgerRowTitle(row.action, row.note, t)}
          </span>
          <span
            className="rounded-lg border px-2 py-0.5 font-mono text-[10px]"
            style={{ borderColor: theme.softBorder, background: theme.chipBg, color: theme.gold }}
          >
            {tokenDisplayLabel(row.asset ?? '')}
          </span>
        </div>
        {row.note ? (
          <div className="mt-0.5 truncate text-xs" style={{ color: theme.muted }}>
            {formatLedgerNote(row.note, t)}
          </div>
        ) : null}
        <div className="mt-0.5 text-xs" style={{ color: theme.muted }}>
          {formatRowTime(row.create_time)}
        </div>
      </div>
      <div className="shrink-0 text-right">
        <div
          className="font-mono text-sm font-bold"
          style={{ color: delta.isNegative ? theme.negative : theme.positive }}
        >
          {delta.text}
        </div>
        <div className="mt-0.5 font-mono text-xs" style={{ color: theme.muted }}>
          {balanceAfterLabel} {row.balance_after ?? '—'}
        </div>
      </div>
    </div>
  );
});
